// An on-screen log, because a phone has no console you can read.
//
// Four attempted fixes for the Android camera swap were reasoned about rather
// than diagnosed, because the only thing that ever reached us was a generic
// string. This collects what actually happened, in a form that can be copied
// out of a phone and pasted somewhere useful.

export type LogLevel = 'info' | 'warn' | 'error';

export type LogEntry = {
  at: number;
  level: LogLevel;
  message: string;
};

const MAX_ENTRIES = 300;

const entries: LogEntry[] = [];
const listeners = new Set<() => void>();

// useSyncExternalStore compares snapshots by identity, so the snapshot has to
// be a stable reference that only changes when the log does. Returning
// entries.slice() on every read looks harmless and renders an infinite loop.
let snapshot: readonly LogEntry[] = [];

function changed(): void {
  snapshot = entries.slice();
  listeners.forEach((listener) => listener());
}

function push(level: LogLevel, parts: unknown[]): void {
  entries.push({ at: Date.now(), level, message: parts.map(describe).join(' ') });
  if (entries.length > MAX_ENTRIES) entries.shift();
  changed();
}

/**
 * Turn anything into something readable.
 *
 * LiveSwitch rejects with its own Exception type: not an Error, often without a
 * usable `message`, and `String(ex)` gives `[object Object]`. That is precisely
 * how a real failure reached us as "LiveSwitch operation failed" and nothing
 * else, so this digs through getters and own properties before giving up.
 */
export function describe(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;
  if (typeof value !== 'object') return String(value);

  const object = value as Record<string, unknown> & {
    getMessage?: () => string;
    message?: string;
    name?: string;
    stack?: string;
  };

  const parts: string[] = [];
  const name = object.name ?? object.constructor?.name;
  if (name && name !== 'Object') parts.push(String(name));

  try {
    const fromGetter = object.getMessage?.();
    if (fromGetter) parts.push(String(fromGetter));
  } catch {
    /* getter threw; keep going */
  }
  if (object.message) parts.push(String(object.message));

  // Own enumerable properties, which is where LiveSwitch keeps its detail.
  try {
    const own = Object.keys(object)
      .filter((key) => !['message', 'name', 'stack'].includes(key))
      .map((key) => {
        const inner = object[key];
        const rendered =
          inner === null || typeof inner !== 'object' ? String(inner) : `[${typeof inner}]`;
        return `${key}=${rendered}`;
      })
      .slice(0, 12);
    if (own.length) parts.push(`{${own.join(', ')}}`);
  } catch {
    /* exotic object */
  }

  if (parts.length === 0) {
    try {
      return JSON.stringify(value).slice(0, 400);
    } catch {
      return Object.prototype.toString.call(value);
    }
  }
  return parts.join(' | ');
}

export const debugLog = {
  info: (...parts: unknown[]) => push('info', parts),
  warn: (...parts: unknown[]) => push('warn', parts),
  error: (...parts: unknown[]) => push('error', parts),
  entries: () => snapshot,
  clear: () => {
    entries.length = 0;
    changed();
  },
  subscribe: (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

let installed = false;

/** Catch what we did not explicitly log: thrown errors and dropped promises. */
export function installDebugCapture(): void {
  if (installed) return;
  installed = true;

  window.addEventListener('error', (event) => {
    debugLog.error('window.error', event.message, `${event.filename}:${event.lineno}`);
  });
  window.addEventListener('unhandledrejection', (event) => {
    debugLog.error('unhandledrejection', describe(event.reason));
  });

  const originalWarn = console.warn.bind(console);
  const originalError = console.error.bind(console);
  console.warn = (...args: unknown[]) => {
    push('warn', args);
    originalWarn(...args);
  };
  console.error = (...args: unknown[]) => {
    push('error', args);
    originalError(...args);
  };
}

/** Everything the panel copies out: environment, build, then the log. */
export function debugReport(): string {
  const lines = [
    `build:      ${__BUILD_ID__}`,
    `url:        ${window.location.href}`,
    `userAgent:  ${navigator.userAgent}`,
    `screen:     ${window.innerWidth}x${window.innerHeight} dpr=${window.devicePixelRatio}`,
    `time:       ${new Date().toISOString()}`,
    '',
  ];
  for (const entry of entries) {
    const at = new Date(entry.at).toISOString().slice(11, 23);
    lines.push(`${at} ${entry.level.toUpperCase().padEnd(5)} ${entry.message}`);
  }
  return lines.join('\n');
}
